import type { PortionSize } from './portion-sizing';

export type CampusGradeConfiguration = {
  code: string;
  display_name: string;
  recommended_portion_size: PortionSize;
  sort_order: number;
  active: boolean;
};

export type CampusMealPlanConfiguration = {
  audience: 'STUDENT' | 'STAFF';
  portion_size: PortionSize;
  display_name: string;
  display_price_cents: number | null;
  sort_order: number;
  active: boolean;
};

export type CampusConfiguration = {
  grades?: CampusGradeConfiguration[];
  meal_plans?: CampusMealPlanConfiguration[];
  show_sales_tax_notice?: boolean;
  sales_tax_rate_basis_points?: number | null;
  meal_plan_prices_include_sales_tax?: boolean;
};

export const selectConfiguredGrades = (configuration?: CampusConfiguration | null) => {
  const configuredGrades = configuration?.grades
    ?.filter((grade) => grade.active)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((grade) => ({ label: grade.display_name, value: grade.code }));

  return configuredGrades ?? [];
};

const formatMealPlanLabel = (plan: CampusMealPlanConfiguration) => {
  if (plan.display_price_cents === null) return plan.display_name;
  return `${plan.display_name} - $${(plan.display_price_cents / 100).toFixed(2)}/meal`;
};

export const selectConfiguredMealPlanOptions = (
  configuration?: CampusConfiguration | null,
  audience: 'STUDENT' | 'STAFF' = 'STUDENT'
) => {
  const configuredPlans = configuration?.meal_plans
    ?.filter((plan) => plan.active && plan.audience === audience)
    .sort((a, b) => a.sort_order - b.sort_order);

  return (configuredPlans ?? []).map((plan) => ({
    label: formatMealPlanLabel(plan),
    value: plan.portion_size,
  }));
};

export const selectConfiguredRecommendedPortion = (
  gradeCode: string,
  configuration?: CampusConfiguration | null
): PortionSize | undefined => {
  const configuredGrade = configuration?.grades?.find((grade) => grade.active && grade.code === gradeCode);

  return configuredGrade?.recommended_portion_size;
};

export const selectConfiguredTaxNotice = (configuration?: CampusConfiguration | null) => {
  if (!configuration?.show_sales_tax_notice || configuration.sales_tax_rate_basis_points == null) return null;

  const rate = (configuration.sales_tax_rate_basis_points / 100).toFixed(2).replace(/\.00$/, '');
  return {
    form: configuration.meal_plan_prices_include_sales_tax
      ? `Below pricing includes ${rate}% sales tax.`
      : `Below pricing does not include ${rate}% sales tax.`,
    menu: configuration.meal_plan_prices_include_sales_tax
      ? 'Displayed meal-plan pricing is inclusive of sales tax.'
      : 'Final price is inclusive of sales tax.',
  };
};
